import { WretchError } from 'wretch/resolver';
import wretch from 'wretch';
import { serializeError } from 'serialize-error';
import { useToast, ToastAction } from 'ui';
import { useTranslations } from 'i11n';
import { useCallback } from 'react';
import { LoginFormData } from '../components/AuthenticateForm';
import { UserNotVerifiedErrorMetadata } from 'bff';

export interface ApiErrorCallbacks<THydrated extends boolean = false> {
  user_not_verified: (
    data: THydrated extends false
      ? { errorMetadata: UserNotVerifiedErrorMetadata }
      : {
          errorMetadata: UserNotVerifiedErrorMetadata;
          credentials: LoginFormData;
        },
  ) => Promise<any>;
}

// A payload can be rejected on many fields at once; past a handful the toast
// stops being readable, and the form already marks every one of them.
const MAX_REPORTED_FIELD_ISSUES = 3;

/**
 * A rejected payload answers with one Zod issue per offending field. Naming
 * them beats the generic "el valor ingresado no es válido", which leaves the
 * user hunting for which field the API actually refused.
 */
const describeFieldIssues = (error: WretchError): string | null => {
  const issues = error?.json?.errors;

  if (!Array.isArray(issues)) return null;

  const messages = Array.from(
    new Set(
      issues
        .map((issue) => issue?.message)
        .filter((message): message is string => typeof message === 'string'),
    ),
  );

  if (!messages.length) return null;

  const reported = messages.slice(0, MAX_REPORTED_FIELD_ISSUES).join('. ');

  return messages.length > MAX_REPORTED_FIELD_ISSUES
    ? `${reported}. Y ${messages.length - MAX_REPORTED_FIELD_ISSUES} más.`
    : reported;
};

export const useApiError = (callbacks?: ApiErrorCallbacks) => {
  const { toast } = useToast();

  const t = useTranslations();

  const handleError = useCallback(
    // `json` is only present when the API answered with a JSON body. A network
    // failure or an aborted request rejects with a plain Error, and reading
    // `.json.code` off it used to throw here — masking the real error behind an
    // unhandled TypeError. Fall back to the generic code instead.
    async (error: WretchError) => {
      const code = error?.json?.code || 'unexpected_error';

      const callback = callbacks?.[code];

      if (callback) {
        await callback({ errorMetadata: error?.json?.metadata });

        return {
          callbackInvoked: true,
        };
      }

      toast({
        title:
          t(`errors.${code}.title`, error?.json?.metadata || {}) ||
          t('errors.unexpected_error.title'),
        description:
          describeFieldIssues(error) ||
          t(`errors.${code}.description`, error?.json?.metadata || {}) ||
          t('errors.unexpected_error.description'),
      });
      return {
        callbackInvoked: false,
      };
    },
    [callbacks],
  );

  const notifyError = useCallback(async (error: WretchError) => {
    wretch()
      .post(
        {
          error: serializeError(
            new Error(
              `Client side error when perform api http call: ${JSON.stringify(
                serializeError(error),
              )}`,
            ),
          ),
        },
        '/api/register/error',
      )
      .json()
      .catch(console.error);
  }, []);

  return {
    handleError,
    notifyError,
  };
};
